import { graphql } from 'gatsby';
import { useLocation, navigate } from '@reach/router';
import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import NewLayout from '../components/new_layout';
import SEO from '../components/seo';
import MainSection100 from '../components/Layout/main-section-100';
import { Container } from '../components/Layout/containers';
import { Tile } from '../components/v2024/tile/tile';
import { CarouselTouch } from '../components/v2024/Carousel/carousel-touch';
// Importa tu componente para EVENTOS
import CardEvent from '../components/v2023/UI/Card/card-event';
// Importa tu componente para WEBINARS
import CardPost from '../components/v2023/UI/Card/card-post';

const EventosAndWebinarsPage = ({ data, pageContext }) => {
	const page = data.page.edges[0].node;
	const lang = pageContext.langKey;
	const now = new Date();

	const location = useLocation();
	const params = new URLSearchParams(location.search);
	const tabFromUrl = params.get('tab');

	// Rutas base, por si es necesario enlazar a la página de Webinars
	const pathWebinars = lang === 'es' ? '/es/webinars' : '/webinars';

	// Estado para controlar el filtro y la paginación
	const defaultTab = tabFromUrl === 'eventos' || tabFromUrl === 'webinars' ? tabFromUrl : 'webinars';

	// Estado para controlar el filtro
	const [activeFilter, setActiveFilter] = useState(defaultTab);

	// Estado para controlar la paginación
	const [visibleCount, setVisibleCount] = useState(10);

	const tecalisEventsSorted = [...data.tecalisEvents.edges].sort((a, b) => {
		if (!a.node.date_start) return -1;
		return new Date(b.node.date_start) - new Date(a.node.date_start);
	});

	const regularEventsSorted = [...data.eventos.edges].sort((a, b) => {
		return new Date(b.node.date_start) - new Date(a.node.date_start);
	});

	//Unificar todos los eventos en un solo array
	const allEvents = [...tecalisEventsSorted, ...regularEventsSorted];

	// 1) Filtrado de destacados
	const featuredEvents = allEvents
		.filter(({ node }) => {
			const isFeatured = node.destacado === 1;
			const isFutureOrNull = !node.date_start || new Date(node.date_start) > now;
			return isFeatured && isFutureOrNull;
		});

	const featuredWebinars = data.webinars.edges
		.filter(({ node }) => node.destacado === 1 && new Date(node.date) > now)
		.sort((a, b) => new Date(a.node.date) - new Date(b.node.date));

	// 2) Definición del carrusel destacado según el filtro
	const carouselSlides =
		activeFilter === 'eventos'
			? featuredEvents.length > 0 ? featuredEvents : allEvents.slice(0, 2)
			: featuredWebinars.length > 0
				? featuredWebinars
				: data.webinars.edges
					.sort((a, b) => new Date(b.node.date) - new Date(a.node.date))
					.slice(0, 2)
					.map(({ node }) => {
						return {
							node: {
								...node,
								url: `${pathWebinars}/${node.url}`,
							},
						};
					});

	// 3) Listado inferior según el filtro
	const listItems = activeFilter === 'webinars' ? data.webinars.edges : allEvents;

	const handleSeeMore = () => {
		setVisibleCount(visibleCount + 10);
	};

	const handleTabChange = (newFilter) => {
		// 1) Actualizamos estado para renderizado inmediato del tab
		setActiveFilter(newFilter);

		// Si quieres reemplazar la entrada del historial en vez de añadir otra,
		// pasa { replace: true }; si quieres que el "Back" del navegador te devuelva al estado anterior, omítelo.
		navigate(`${location.pathname}?tab=${newFilter}`, { replace: true });
	};

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const tab = params.get('tab');
		if (tab && (tab === 'eventos' || tab === 'webinars')) {
			setActiveFilter(tab);
		}
	}, [location.search]);

	return (
		<NewLayout pageContext={pageContext} lang={lang} translates={pageContext?.translates}>
			<SEO
				lang={lang}
				title={page.seo.title}
				description={page.seo.meta_description}
				translates={pageContext.translates}
				image={page.seo.image_2.localFile.publicURL}
			/>

			<main className="main" key={activeFilter}>
				<MainSection100 sectionType="white" sectionColor="brown">
					<h1 lang="es" className="hero">
						{page.titulo}
					</h1>
					<div className="container__text">
						<ReactMarkdown children={page.descripcion} rehypePlugins={[rehypeRaw]} />
					</div>
					<div className="tabs tabs--pills-big">
						<h5 className={`tabs__tab ${activeFilter === 'webinars' ? 'tabs__tab--active' : ''}`}>
							<button onClick={() => handleTabChange('webinars')}>
								{lang === 'es' ? 'Webinars' : 'Webinars'}
							</button>
						</h5>
						<h5 className={`tabs__tab ${activeFilter === 'eventos' ? 'tabs__tab--active' : ''}`}>
							<button onClick={() => handleTabChange('eventos')}>
								{lang === 'es' ? 'Eventos' : 'Events'}
							</button>
						</h5>
					</div>
				</MainSection100>

				{carouselSlides.length > 0 && (
					<MainSection100 sectionType="color" sectionColor="gray" grayNograph noFrame>
						{/* Si el filtro activo es 'eventos', usamos <CardEvent> en el carrusel */}
						{carouselSlides.length > 0 && activeFilter === 'eventos' && (
							<>
								{/* <h2>{lang === "es" ? "Eventos Destacados" : "Featured Events"}</h2> */}
								<CarouselTouch
									lang={lang}
									slides={carouselSlides}
									showArrows
									showIndicators
									/*hideShowMore*/
									useCardEvent
									useCustomCarousel
								/>
							</>
						)}
						{/* Si el filtro activo es 'webinars', usamos <CardPost> en el carrusel */}
						{carouselSlides.length > 0 && activeFilter === 'webinars' && (
							<>
								{/* <h2>{lang === "es" ? "Webinars Destacados" : "Featured Webinars"}</h2> */}
								<CarouselTouch
									lang={lang}
									slides={carouselSlides}
									showArrows
									showIndicators
									/*hideShowMore*/
									useCardPost
									useCustomCarousel
									useShowMore2={true}
								/>
							</>
						)}
					</MainSection100>
				)}

				{/* Mostrar sección solo con mensaje si no hay webinars en inglés */}
				{carouselSlides.length === 0 && activeFilter === 'webinars' && lang === 'en' && (
					<MainSection100>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="128"
							height="128"
							viewBox="0 0 128 128"
							fill="none"
						>
							<path
								d="M112 64V122H16V64"
								stroke="#C9C5D1"
								stroke-width="4"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
							<path
								d="M122 46H6V64H122V46Z"
								stroke="#C9C5D1"
								stroke-width="4"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
							<path
								d="M53.334 86H74.6673"
								stroke="#C9C5D1"
								stroke-width="4"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
							<line
								x1="62"
								y1="30"
								x2="62"
								y2="4"
								stroke="#C9C5D1"
								stroke-width="4"
								stroke-linecap="round"
							/>
							<line
								x1="80.1439"
								y1="28.9583"
								x2="89.2935"
								y2="13.4572"
								stroke="#C9C5D1"
								stroke-width="4"
								stroke-linecap="round"
							/>
							<line
								x1="2"
								y1="-2"
								x2="20"
								y2="-2"
								transform="matrix(-0.50831 -0.861174 -0.861174 0.50831 45.2832 31.6973)"
								stroke="#C9C5D1"
								stroke-width="4"
								stroke-linecap="round"
							/>
						</svg>
						<h3>There are no webinars available in English</h3>
					</MainSection100>
				)}

				{/* Listado inferior (usando <CardEvent> para eventos y <CardPost> para webinars) */}
				{listItems.length > 0 && (
					<MainSection100>
						<div className="grid-lg-2">
							{listItems.slice(0, visibleCount).map((item, index) => {
								if (activeFilter === 'webinars') {
									// Renderizamos WEBINARS con CardPost
									const webinar = item.node;
									return (
										<CardPost
											key={index}
											lang={lang}
											// Asumiendo que uses una ruta /webinars para enlazar
											url={`${pathWebinars}/${webinar.url}`}
											// Ajusta la propiedad de la imagen según tu modelo (photo_2, thumbnail_2, etc.)
											image={webinar.thumbnail_2}
											name={webinar.title}
											// Ajusta según tu helper, e.g. getFirstText(webinar.seo.meta_description) o webinar.description
											description={webinar.seo?.meta_description || webinar.description}
											big
										/>
									);
								} else {
									// Renderizamos EVENTOS con CardEvent
									return (
										<CardEvent
											key={index}
											event={item.node}
											showDescription={true}
											lang={lang}
											principal
										/>
									);
								}
							})}
						</div>

						{listItems.length > visibleCount && (
							<div className="container__see-more">
								<button className="button button--xl button--secondary" onClick={handleSeeMore}>
									{lang === 'es' ? 'Ver más' : 'See more'}
								</button>
							</div>
						)}
					</MainSection100>
				)}

				<MainSection100>
					<div className="grid-lg-2">
						<Tile
							title={page.customer_stories.name}
							body={page.customer_stories.description}
							image={page.customer_stories.image}
							button={{ text: page.customer_stories.button, url: page.customer_stories.url }}
						/>
						<Tile
							title={page.solicita_demo.name}
							body={page.solicita_demo.description}
							image={page.solicita_demo.image}
							button={{ text: page.solicita_demo.button, url: page.solicita_demo.url }}
							purple
						/>
					</div>
				</MainSection100>
			</main>
		</NewLayout>
	);
};

export default EventosAndWebinarsPage;

export const EventosAndWebinarsQuery = graphql`
	query ($langKey: String) {
		page: allStrapiEventosAndWebinars(filter: { locale: { eq: $langKey } }) {
			edges {
				node {
					id
					titulo
					descripcion
					locale
					published_at
					created_at
					updated_at
					seo {
						title
						meta_description
						image_2 {
							localFile {
								publicURL
								childImageSharp {
									gatsbyImageData(quality: 100, placeholder: BLURRED, formats: [WEBP])
								}
							}
						}
					}
					customer_stories {
						id
						name
						description
						button
						url
						blank
						image {
							localFile {
								childImageSharp {
									gatsbyImageData(width: 334, quality: 100, placeholder: BLURRED, formats: [WEBP])
								}
							}
							alternativeText
						}
					}
					solicita_demo {
						id
						name
						description
						button
						url
						blank
						image {
							localFile {
								childImageSharp {
									gatsbyImageData(width: 334, quality: 100, placeholder: BLURRED, formats: [WEBP])
								}
							}
							alternativeText
						}
					}
					localizations {
						id
						locale
						published_at
					}
				}
			}
		}
		eventos: allStrapiEvents(filter: { locale: { eq: $langKey } }, sort: { fields: [date_start], order: DESC }) {
			edges {
				node {
					seo {
						meta_description
					}
					title
					description
					url
					date_start
					date_end
					destacado
					image_preview {
						localFile {
							childImageSharp {
								gatsbyImageData(quality: 100, placeholder: BLURRED, formats: [WEBP])
							}
						}
					}
					location {
						city
					}
				}
			}
		}
		tecalisEvents: allStrapiTecalisEvents(filter: { locale: { eq: $langKey } }, sort: { fields: [date_start], order: DESC }) {
			edges {
				node {
					seo {
						meta_description
					}
					title
					hero_description
					url
					date_start
					date_end
					location {
						city
					}
					image_preview {
						localFile {
							childImageSharp {
								gatsbyImageData(quality: 100, placeholder: BLURRED, formats: [WEBP])
							}
						}
					}
				}
			}
		}
		webinars: allStrapiWebinars(filter: { locale: { eq: $langKey } }, sort: { fields: [date], order: DESC }) {
			edges {
				node {
					seo {
						meta_description
					}
					url
					date
					title
					description
					destacado
					# Ajusta el nombre del campo de imagen que uses (por ejemplo photo_2, thumbnail_2, etc.)
					thumbnail_2 {
						localFile {
							childImageSharp {
								gatsbyImageData(
									breakpoints: [636, 520, 512, 350]
									width: 666
									quality: 100, 
									placeholder: BLURRED, 
									formats: [WEBP]
								)
							}
						}
					}
				}
			}
		}
	}
`;
